import { Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core'
import { Router, ActivatedRoute } from '@angular/router'
import {
  Customer,
  PriceList,
  Product,
  Service,
  Account,
  Coupon,
  Subscription,
  MultipassSubscription,
} from '../../types'
import moment from 'moment'
import SubscriptionDataService from './subscription-data.service'
import { prettyCents, prettyDollars, availableCouponApplications } from 'src/logic/pricing'
import { MediaQueryService } from '../../services/media-query-service'
import { BaseComponent } from 'src/components/base.component'
import { errorToast, successToast } from '../../toaster'
import { sweetAlert } from 'bmAlert'
import { fullDayWithYear } from '../../utils'
import { FormControl } from '@angular/forms'
import { EnhancementAlertHelper } from '../shared/enhancement-alert.helper'
import { ISummaryData } from 'src/interfaces'
import { openCommitmentModal, openValetModal, openWordLenghtModal } from './modals'
import {
  ICustomWordCount,
  ISelectedContent,
  ISelectedProduct,
  ISubscriptionConfig,
} from './interfaces'
import { AddOnCode, DiscountPerCommitment, MonthsToString } from './enums'
import { AddOnIcon } from '../delivery-options/enums'

const ONE_DOLLAR = 100

@Component({
  selector: 'multipass-plan-form',
  templateUrl: './multipass-plan.component.html',
  styleUrls: ['./multipass-plan.component.scss'],
})
export class MultipassPlanComponent extends BaseComponent {
  @Input() set customerId(value: number) {
    this._customerId = value
    value != null ? this.setDataWithCustomerId() : this.setDataWithoutCustomerId()
  }
  get customerId(): number {
    return this._customerId
  }
  @Output() onCreate: EventEmitter<boolean> = new EventEmitter()
  @Output() onSubmitwww: EventEmitter<string> = new EventEmitter()

  @ViewChild('section2') private section2: ElementRef
  @ViewChild('section3') private section3: ElementRef
  @ViewChild('section4') private section4: ElementRef
  @ViewChild('section5') private section5: ElementRef

  moment = moment

  _customerId: number
  customer: Customer
  priceList: PriceList
  priceListLoaded = false

  actionButton = 'save'
  saveButtonText: string
  isSaveButtonDisabled = false
  errorMessage: string
  confirmationMessage1: string
  confirmationMessage2: string
  confirmationMessage3: string
  email = new FormControl('')

  contentSubtotalCredits = 0
  contentSubtotalFormattedValue = this.currencyFormat(0)

  addOnsSubtotalCredits = 0
  addOnsSubtotalFormattedValue = this.currencyFormat(0)

  monthlyPlanTotalFormattedValue = this.currencyFormat(0)
  quarterlyPlanTotalFormattedValue = this.currencyFormat(0)
  annuallyPlanTotalFormattedValue = this.currencyFormat(0)

  monthlyDisabled = false
  videoModalVisible = false

  account: Account

  subscriptionConfig: ISubscriptionConfig = {
    products: {},
    addOns: {},
    services: {},
    durationInMonths: null,
  }

  productCodesToShow = ['content300', 'content600', 'content1000', 'content1500', 'content2000']
  addOnCodes: AddOnCode[] = Object.values(AddOnCode)
  addOnIcons = AddOnIcon
  customWordCounts: ICustomWordCount[] = []
  discounts = DiscountPerCommitment

  subTotalFormattedValue = this.currencyFormat(0)
  valetFormattedValue = this.currencyFormat(0)
  discountsFormattedValue = this.currencyFormat(0)
  discountApplied = 0
  subTotalWithoutCouponsOrValet = 0
  totalCredits = 0
  totalFormattedValue = this.currencyFormat(0)

  prevTotalCredits = 0
  plusValetChecked = false
  nextButtonIsActive = false
  showUpTo = 1
  totalCents = 0

  showActivateButton: boolean
  mainTitle = 'New plan'
  summaryData: ISummaryData
  pastDue = false

  openValetModal = openValetModal
  openCommitmentModal = openCommitmentModal
  openWordLenghtModal = openWordLenghtModal

  private usableCoupons: Coupon[] = []
  private hasCoupons = false
  private plusValetTierCode: string
  private prevPlusPhotoChecked: boolean
  private prevPlusValetChecked: boolean
  private prevDurationInMonths: number
  private prevCurrentPeriodEndsAt: moment.Moment
  private summaryModifier: string

  private paymentDay: string
  private nextRenewalDate: string
  private isDecreasingPlan = false
  private isIncreasingPlan = false
  private paymentFailedVerboseMessage = `<div class="m-3 small text-muted">Don't worry, we'll save your progress so you can complete this subscription when ready.</div>`

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private subscriptionDataService: SubscriptionDataService,
    private mediaQuery: MediaQueryService
  ) {
    super()
  }

  fullDayWithYear = fullDayWithYear

  private setDataWithCustomerId(): void {
    const customerId = this.customerId
    const priceListObs = this.subscriptionDataService
      .getPriceListObserver()
      .subscribe((data: { priceList: any }) => {})

    const subscriptionObs = this.subscriptionDataService
      .getSubscriptiontObserver()
      .subscribe((data: { customer: Customer }) => {
        // Clean custom word counts on every subscription load
        this.customWordCounts = []
        if (data) {
          // If customer is not Multipass, redirect to the corresponding page
          if (!data.customer.multipass && !data.customer.canBeMigratedFromExpired) {
            this.router.navigate(['../plan'], { relativeTo: this.route })
          }

          this.account = data.customer.account
          this.customer = data.customer
          this.pastDue = this.setPastDue()

          // Set form values from active subscription
          const subscriptionToLoad =
            data.customer.reactivationSubscription ||
            data.customer.scheduledSubscription ||
            data.customer.subscription

          // Set price list
          this.setPriceList(subscriptionToLoad?.priceList || data.customer.priceList)

          // Initialize form values
          this.setMainTitle()

          if (this.customerId && !this.customer.subscription && localStorage.getItem('pricing')) {
            this.loadPreviousSelections()
          } else {
            this.setFormValues(subscriptionToLoad)
          }

          // Execute validations and recalculate values
          this.setUsableCoupons()
          this.handleAddOnChange('optimize')
          this.handlePlusValetChange()
          this.prevTotalCredits = this.totalCredits
          this.prevPlusValetChecked = this.plusValetChecked
          this.prevDurationInMonths = this.subscriptionConfig.durationInMonths

          this.priceListLoaded = true
          this.calculateRenewalDates()
          this.calculateSubTotals()
        }
      })
    this.observers.push(priceListObs)
    this.observers.push(subscriptionObs)

    this.subscriptionDataService.executeQuery({ customerId })
  }

  private setDataWithoutCustomerId(): void {
    const priceListObs = this.subscriptionDataService
      .getPriceListObserver()
      .subscribe((data: { priceList: PriceList }) => {
        if (data) {
          this.setPriceList(data.priceList)
          // Initialize form values
          this.subscriptionConfig.durationInMonths = null

          this.productCodesToShow.forEach((productCode: string) => {
            const product: Product = this.priceList.products[productCode]
            this.subscriptionConfig.products[productCode] = {
              id: product.productId,
              quantity: 0,
              words: product.words,
              addOns: {},
            }
          })
          this.addOnCodes.forEach((addOnCode) => {
            this.subscriptionConfig.addOns[addOnCode] = {
              checked: false,
              disabled: false,
            }
          })
          this.prevPlusPhotoChecked = false
          this.plusValetChecked = false
          this.showUpTo = 99

          // Execute recalculate values
          this.priceListLoaded = true
          this.calculateRenewalDates()
          this.calculateSubTotals()
        }
      })
    const default2023PriceListId = 109
    this.observers.push(priceListObs)
    this.subscriptionDataService.executeQuery({ priceListId: default2023PriceListId })
  }

  private setPriceList(priceList: any): void {
    this.priceList = {
      creditPriceInCents: priceList.creditPriceInCents,
      products: {},
      addOns: {},
      services: {},
    }

    priceList.products.forEach((product: any) => {
      product.formattedValue = this.currencyFormat(
        product.valueInCredits * priceList.creditPriceInCents
      )
      let _product = {
        id: product.id,
        quantity: 0,
        addOns: {},
      }
      const addOnsArr = Array.isArray(product.addOns)
        ? product.addOns
        : Object.values(product.addOns)
      product.addOns = addOnsArr.reduce((acc: object, addOn: any) => {
        if (
          this.priceList.addOns[addOn.code] == undefined ||
          addOn.valueInCredits < this.priceList.addOns[addOn.code]?.valueInCredits
        ) {
          addOn.formattedValue = this.currencyFormat(
            addOn.valueInCredits * this.priceList.creditPriceInCents
          )
          this.priceList.addOns[addOn.code] = addOn
        }
        acc[addOn.code] = addOn
        return acc
      }, {})
      this.priceList.products[product.code] = product
      this.subscriptionConfig.products[product.code] ||= _product
    })
    priceList.services.forEach((service: Service) => {
      this.priceList.services[service.code + (service.forSubsWithCreditsUpTo || 0)] = service
    })

    this.calculateSubTotals()
  }

  handleCancelChanges(): void {
    this.isSaveButtonDisabled = true
    this.subscriptionDataService
      .cancelScheduledMultipassSubscriptionMutation(this.customer.id)
      .then((result) => {
        successToast('Changes canceled')
        this.customer.scheduledSubscription = null
        this.calculateSubTotals()
      })
      .finally(() => {
        this.isSaveButtonDisabled = false
      })
  }

  handleUndoCancel(): void {
    this.isSaveButtonDisabled = true
    this.subscriptionDataService
      .undoCancelMultipassSubscriptionMutation(this.customer.id)
      .then((result) => {
        successToast('This subscription is no longer scheduled to cancel')
      })
      .finally(() => {
        this.isSaveButtonDisabled = false
      })
  }

  handleQuantityChange(): void {
    this.calculateSubTotals()
  }

  handleNextClick(): void {
    this.showUpTo++
    this.calculateSubTotals()
    const allSections = [this.section2, this.section3, this.section4, this.section5]
    setTimeout(() => {
      allSections[this.showUpTo - 2].nativeElement.scrollIntoView({
        behavior: 'smooth',
      })
    }, 100)
  }

  private updateVideoModalVisible() {
    this.videoModalVisible = !this.videoModalVisible
  }

  openEnhancementsModal($event: Event, addOnCode: string): void {
    EnhancementAlertHelper.openEnhancementsModal($event, addOnCode, () =>
      this.updateVideoModalVisible()
    )
  }

  addCustomWordCount(): void {
    const content400: Product = Object.values(this.priceList.products).find(
      (content) => content.words === 400
    )
    this.customWordCounts.push({
      ...content400,
      quantity: 0,
    })
  }

  deleteCustomWordCount(index: number): void {
    this.customWordCounts.splice(index, 1)
    this.handleQuantityChange()
  }

  increaseCustomWordCount(customWordCount: Product, index: number): void {
    const found = Object.values(this.priceList.products).find(
      (content) => content.words === customWordCount.words + 100
    )
    this.customWordCounts[index] = {
      quantity: this.customWordCounts[index].quantity,
      ...found,
    }
  }

  decreaseCustomWordCount(customWordCount: Product, index: number): void {
    const found = Object.values(this.priceList.products).find(
      (content) => content.words === customWordCount.words - 100
    )
    this.customWordCounts[index] = {
      quantity: this.customWordCounts[index].quantity,
      ...found,
    }
  }

  private calculateSubTotals(): void {
    let _contentSubtotalCredits = 0
    let _addOnsSubtotalCredits = 0

    // logic for custom words
    this.customWordCounts.forEach((item) => {
      const { addOns, quantity, valueInCredits } = item

      _contentSubtotalCredits += quantity * valueInCredits

      Object.values(addOns).forEach((addOn) => {
        let totalAddOn = 0
        const addOnChecked = this.subscriptionConfig.addOns[addOn['code']].checked
        const sumThisAddOn =
          addOnChecked &&
          !(this.subscriptionConfig.addOns['optimize'].checked && addOn['code'] == 'photo')

        if (sumThisAddOn) totalAddOn += quantity * addOn['valueInCredits'] || 0

        _addOnsSubtotalCredits += totalAddOn
      })
    })

    // logic for normal words (products)
    Object.entries(this.subscriptionConfig.products).forEach(([code, item]) => {
      let productQtty = item['quantity'] || 0
      if (productQtty) {
        _contentSubtotalCredits += productQtty * this.priceList.products[code]?.valueInCredits

        const stc = Object.keys(this.subscriptionConfig.addOns).reduce((acc, addOnCode) => {
          const addOnChecked = this.subscriptionConfig.addOns[addOnCode].checked
          const sumThisAddOn =
            addOnChecked &&
            !(this.subscriptionConfig.addOns['optimize'].checked && addOnCode == 'photo')
          let totalAddOn = 0
          if (sumThisAddOn)
            totalAddOn =
              productQtty * (this.priceList.products[code]?.addOns[addOnCode]?.valueInCredits || 0)
          return acc + totalAddOn
        }, 0)
        _addOnsSubtotalCredits += stc
      }
    })

    this.contentSubtotalCredits = _contentSubtotalCredits
    this.contentSubtotalFormattedValue = this.currencyFormat(
      _contentSubtotalCredits * this.priceList.creditPriceInCents
    )

    let _totalCredits = _addOnsSubtotalCredits + _contentSubtotalCredits

    let [_valetTierCode, _valetTier] = Object.entries(this.priceList.services).find(
      ([code, service]) => {
        if (code.startsWith('valet'))
          return service.forSubsWithCreditsUpTo >= _totalCredits || !service.forSubsWithCreditsUpTo
      }
    )

    this.plusValetTierCode = _valetTierCode
    let _valetTotalCents = _valetTier.valueInCents
    this.valetFormattedValue = this.currencyFormat(_valetTotalCents)
    !this.plusValetChecked ? (_valetTotalCents = 0) : _valetTotalCents

    this.addOnsSubtotalCredits = _totalCredits
    this.addOnsSubtotalFormattedValue = this.currencyFormat(
      _totalCredits * this.priceList.creditPriceInCents
    )

    let _subTotalCents = _totalCredits * this.priceList.creditPriceInCents

    const commitment = MonthsToString[this.subscriptionConfig.durationInMonths]
    this.discountApplied = this.discounts[commitment] || 0
    this.subTotalWithoutCouponsOrValet = _subTotalCents * (1 - this.discountApplied)

    let couponsDiscountCents = 0
    this.usableCoupons.forEach((coupon) => {
      couponsDiscountCents += this.percentCouponDiscount(coupon)
    })
    this.totalCents = this.subTotalWithoutCouponsOrValet + _valetTotalCents - couponsDiscountCents

    this.monthlyPlanTotalFormattedValue = this.currencyFormat(
      _subTotalCents * (1 - this.discounts.oneMonth)
    )
    this.quarterlyPlanTotalFormattedValue = this.currencyFormat(
      _subTotalCents * (1 - this.discounts.threeMonth)
    )
    this.annuallyPlanTotalFormattedValue = this.currencyFormat(
      _subTotalCents * (1 - this.discounts.oneYear)
    )

    this.subTotalFormattedValue = this.currencyFormat(_subTotalCents)
    this.discountsFormattedValue = this.currencyFormat(_subTotalCents * this.discountApplied)
    this.totalCredits = _totalCredits
    this.totalFormattedValue = this.currencyFormat(this.totalCents)

    const isNotPendingSubscription =
      this.prevTotalCredits && this.customer?.subscription?.state != 'pending'

    if (isNotPendingSubscription) {
      const decreasingCreditAmount = this.totalCredits < this.prevTotalCredits
      const decreasingDuration =
        this.subscriptionConfig.durationInMonths < this.prevDurationInMonths
      const removingValet = this.plusValetChecked < this.prevPlusValetChecked

      this.isDecreasingPlan = decreasingCreditAmount || decreasingDuration || removingValet

      if (!this.isDecreasingPlan) {
        const increasingCreditAmount = this.totalCredits > this.prevTotalCredits
        const increasingDuration =
          !!this.prevDurationInMonths &&
          this.subscriptionConfig.durationInMonths > this.prevDurationInMonths
        const addingValet = this.plusValetChecked > this.prevPlusValetChecked
        this.isIncreasingPlan = increasingCreditAmount || increasingDuration || addingValet
      }
    }

    this.calculateRenewalDates()
    this.checkSubscriptionModification()

    this.nextButtonIsActive =
      this.contentSubtotalCredits > 0 &&
      (this.showUpTo != 4 || this.subscriptionConfig.durationInMonths != null)

    this.handleShowActivateButton()
  }

  private handleShowActivateButton(): void {
    const isNotZeroDollar = this.totalCents >= ONE_DOLLAR
    const isValetWithNoCredits = this.plusValetChecked && this.totalCredits <= 0

    this.showActivateButton = isNotZeroDollar && !isValetWithNoCredits
    this.errorMessage = isValetWithNoCredits
      ? 'A plan must include at least 1 piece of content, please update the above to continue.'
      : 'A plan cannot be $0, please update the above to continue.'
  }

  private calculateRenewalDates(): void {
    this.paymentDay = moment().date().toString()

    let months: number = this.subscriptionConfig?.durationInMonths

    if (this.isDecreasingPlan) {
      months = this.customer?.subscription?.durationInMonths
    } else if (this.isIncreasingPlan) {
      months = 1
    }

    if (this.customer && this.customer.subscription?.nextCreditsIssuedAt) {
      const date = this.customer.subscription?.nextCreditsIssuedAt
      this.nextRenewalDate = moment(date).format('Do')
    } else {
      this.nextRenewalDate = moment().add(months, 'M').format('MMMM Do, YYYY')
    }
  }

  private checkSubscriptionModification(): void {
    this.actionButton = 'save'
    this.saveButtonText = 'Activate plan'
    this.summaryModifier = null

    this.confirmationMessage1 = `You'll be charged now, and on the ${moment().format('Do')} of every month`
    if (this.subscriptionConfig.durationInMonths > 1) {
      this.confirmationMessage1 += ` for ${this.subscriptionConfig.durationInMonths} months, renewing on ${this.nextRenewalDate}`
    }
    this.confirmationMessage1 += '.'
    this.confirmationMessage2 = ''
    this.confirmationMessage3 = 'By activating your plan'
    const periodicities = { 1: 'monthly', 3: 'quarterly', 12: 'annual' }
    const isNewCustomer: boolean = !this.customer?.subscription?.state

    if (
      (isNewCustomer || this.customer?.subscription?.state === 'pending') &&
      this.router.url.includes('signup')
    ) {
      this.saveButtonText = 'Next'
    }

    if (this.prevTotalCredits && this.customer?.subscription?.state != 'pending') {
      const hasMissingFields: boolean =
        !this.subscriptionConfig.durationInMonths || !this.totalCredits

      if (this.isDecreasingPlan && !isNewCustomer) {
        this.summaryModifier = 'decrease'
        this.saveButtonText = 'Schedule decrease'
        this.confirmationMessage1 = `Your plan decrease will take effect when your ${
          periodicities[this.prevDurationInMonths]
        } commitment ends on ${this.nextRenewalDate}.`
        this.confirmationMessage3 = 'By scheduling your plan change'
      } else if (this.isIncreasingPlan && !isNewCustomer) {
        this.summaryModifier = 'increase'
        this.saveButtonText = 'Schedule increase'
        const daysUntilBilling = this.prevCurrentPeriodEndsAt.diff(moment(), 'days')
        this.confirmationMessage1 = `Your plan increase will begin with your next invoice in ${daysUntilBilling} days.`
        this.confirmationMessage2 = `Your ${
          periodicities[this.prevDurationInMonths]
        } commitment will continue to end on ${this.nextRenewalDate}.`
        this.confirmationMessage3 = 'By scheduling your plan change'
      } else if (this.customer.scheduledSubscription) {
        this.confirmationMessage1 = `You've scheduled this plan to take effect on ${moment(
          this.customer.scheduledSubscription.currentPeriodStartedAt
        ).format('MMMM Do, YYYY')}.`
        this.confirmationMessage2 = ''
        this.confirmationMessage3 = 'By canceling your scheduled plan change'
        this.actionButton = 'cancel_scheduled'
      } else if (hasMissingFields) {
        this.actionButton = null
      }
    }

    this.updateSummaryData()
  }

  handleAddOnChange(code: string): void {
    if (code == 'optimize') {
      if (this.subscriptionConfig.addOns['optimize'].checked) {
        this.prevPlusPhotoChecked = this.subscriptionConfig.addOns['photo'].checked
        this.subscriptionConfig.addOns['photo'].checked = true
        this.subscriptionConfig.addOns['photo'].disabled = true
      } else {
        this.subscriptionConfig.addOns['photo'].checked = this.prevPlusPhotoChecked
        this.subscriptionConfig.addOns['photo'].disabled = false
      }
    } else if (code == 'photo') {
      this.prevPlusPhotoChecked = true
      this.subscriptionConfig.addOns['photo'].checked = this.prevPlusPhotoChecked
      this.subscriptionConfig.addOns['photo'].disabled = false
    }

    this.calculateSubTotals()
  }

  handlePlusValetChange(): void {
    if (this.plusValetChecked) {
      if (this.subscriptionConfig.durationInMonths == 1) {
        this.subscriptionConfig.durationInMonths = 3
      }
      this.monthlyDisabled = true
    } else {
      this.monthlyDisabled = false
    }

    this.calculateSubTotals()
  }

  handleTermChange(): void {
    this.calculateSubTotals()
  }

  handleActivatePlanClick(): void {
    this.isSaveButtonDisabled = true
    this.saveSubscription()
  }

  handleReactivateSubscription(): void {
    this.isSaveButtonDisabled = true
    this.reactivateSubscription()
  }

  private saveSubscription(): void {
    const subscription = {
      products: [],
      services: [],
      durationInMonths: this.subscriptionConfig.durationInMonths,
      saveAsPending: false,
    }

    Object.entries(this.subscriptionConfig.products).forEach(([code, product]) => {
      if (product['quantity'] > 0) {
        const _addOns = []
        Object.entries(this.priceList.products[code].addOns).forEach(([addOnCode, addOn]) => {
          if (this.subscriptionConfig.addOns[addOnCode].checked)
            _addOns.push({
              id: addOn['addOnId'],
              quantity: product['quantity'],
            })
        })
        subscription.products.push({
          id: product['id'],
          quantity: product['quantity'],
          addOns: _addOns,
        })
      }
    })
    if (this.plusValetChecked)
      subscription.services.push({
        id: this.priceList.services[this.plusValetTierCode].serviceId,
        quantity: 1,
      })

    //Logic to merge custom word counts into existing products
    this.customWordCounts.forEach((customWordCount) => {
      const { addOns } = customWordCount
      const productIndex = subscription.products.findIndex(
        (product) => product.id == customWordCount.productId
      )

      if (productIndex > -1) {
        subscription.products[productIndex].quantity += customWordCount.quantity
        // stack quantity in existing addOns
        Object.values(addOns).forEach((addOn) => {
          const addOnIndex = subscription.products[productIndex].addOns.findIndex(
            (productAddOn) => productAddOn.id == addOn['addOnId']
          )
          if (addOnIndex > -1) {
            subscription.products[productIndex].addOns[addOnIndex].quantity +=
              customWordCount.quantity
          }
        })
      } else {
        if (customWordCount.quantity == 0) return

        const _addOns = []

        Object.values(addOns).forEach((addOn) => {
          if (this.subscriptionConfig.addOns[addOn['code']].checked)
            _addOns.push({
              id: addOn['addOnId'],
              quantity: customWordCount['quantity'],
            })
        })

        subscription.products.push({
          id: customWordCount.productId,
          quantity: customWordCount.quantity,
          addOns: _addOns,
        })
      }
    })

    this.subscriptionDataService.createSubscription(null, this.customer.id, subscription).then(
      (result) => {
        this.updateSummaryData()
        this.summaryData.priceListLoaded = true

        localStorage.removeItem('pricing')
        localStorage.removeItem('customWordCounts')
        localStorage.removeItem('plusValetChecked')
        localStorage.setItem('summary', JSON.stringify(this.summaryData))
        successToast('Subscription saved')
        this.onCreate.emit(true)
      },
      (errors) => {
        const errorMessage = errors[0]
        if (this.isPaymentError(errorMessage)) {
          sweetAlert
            .openPaymentFailedModal(this.paymentFailedVerboseMessage)
            .then(({ value: clickedUpdatePaymentButton, dismiss }) => {
              if (clickedUpdatePaymentButton) {
                subscription.saveAsPending = true
                this.subscriptionDataService
                  .createSubscription(null, this.customer.id, subscription)
                  .then((data) => {
                    this.navigateToPaymentPage(this.customer.id)
                  })
              }
            })
        } else {
          errors.forEach((e) => errorToast(e))
        }
      }
    ).finally(() => {
      this.isSaveButtonDisabled = false
    })
  }

  reactivateSubscription(): void {
    this.subscriptionDataService.reactivateSubscription(null, this.customer.id).then(
      (result) => {
        successToast('Subscription reactivated')
        this.onCreate.emit(true)
      },
      (errors) => {
        const errorMessage = errors[0]
        if (this.isPaymentError(errorMessage)) {
          sweetAlert
            .openPaymentFailedModal(this.paymentFailedVerboseMessage)
            .then(({ value: clickedUpdatePaymentButton, dismiss }) => {
              if (clickedUpdatePaymentButton) {
                this.subscriptionDataService
                  .reactivateSubscription(null, this.customer.id, true)
                  .then((data) => {
                    this.navigateToPaymentPage(this.customer.id)
                  })
              }
            })
        } else {
          errors.forEach((e) => errorToast(e))
        }
      }
    ).finally(() => {
      this.isSaveButtonDisabled = false
    })
  }

  saveAndContinue(): void {
    this.email.markAsTouched()

    if (this.email.invalid) {
      return
    }

    this.saveSelections()
    this.onSubmitwww.emit(this.email.value)
  }

  private saveSelections(): void {
    if (!this.customerId) {
      localStorage.setItem('pricing', JSON.stringify(this.subscriptionConfig))
      localStorage.setItem('plusValetChecked', this.plusValetChecked.toString())
      localStorage.setItem('customWordCounts', JSON.stringify(this.customWordCounts))
    }
  }

  private isPaymentError(errorMessage: string): boolean {
    return errorMessage.startsWith('Purchase failed')
  }

  private navigateToPaymentPage(customerId): void {
    successToast('Your request has been saved as a draft')
    const returnUrl = `/dashboard/${customerId}/multipass-plan`
    // CanDeactivateKeywordPage ignores this route
    this.router.navigate(['/dashboard', 'billing_info', 'edit'], {
      queryParams: { editing: true, returnUrl },
    })
  }

  private setMainTitle(): void {
    if (this.customer?.subscription?.state === 'pending') {
      this.mainTitle = 'Activate Plan'
    } else if (!!this.customer?.subscription?.state) {
      this.mainTitle = 'Change Plan'
    }
  }

  private loadPreviousSelections(): void {
    const selectionsJson = localStorage.getItem('pricing')
    const selectionCustomWordCounts = localStorage.getItem('customWordCounts')
    const selectionPlusValetChecked = localStorage.getItem('plusValetChecked')
    if (selectionsJson) {
      this.subscriptionConfig = JSON.parse(selectionsJson)
      this.showUpTo = 99
    }
    if (selectionCustomWordCounts) this.customWordCounts = JSON.parse(selectionCustomWordCounts)

    if (selectionPlusValetChecked) this.plusValetChecked = JSON.parse(selectionPlusValetChecked)
  }

  private loadCustomWordCount(product: ISelectedContent): void {
    const customWordCountCode = parseInt(product.code.split('content')[1])
    const content: Product = Object.values(this.priceList.products).find(
      (product: Product) => product.words === customWordCountCode
    )

    // if valet exist in the product, set the valet checkbox to true
    if (product?.code === 'valet') this.plusValetChecked = true

    //this validation is to avoid an error trying to handle undefined data due the valet product
    if (content) {
      const { addOns, formattedValue, productId, valueInCredits, words } = content

      this.customWordCounts.push({
        id: product.id,
        quantity: product.quantity,
        code: product.code,
        addOns,
        formattedValue,
        productId,
        valueInCredits,
        words,
      })

      product.selectedAddOns?.forEach((addOn) => {
        if (this.subscriptionConfig.addOns[addOn.code]) {
          // this validation is because prevPlusPhotoChecked change the value of the checkbox
          if (addOn.code === 'photo') this.handleAddOnChange('photo')
          this.subscriptionConfig.addOns[addOn.code].checked = true
        }
      })
    }
  }

  private setFormValues(subscriptionToLoad: MultipassSubscription | Subscription) {
    this.subscriptionConfig.durationInMonths = null
    this.productCodesToShow.forEach((productCode) => {
      const product = this.priceList.products[productCode]

      this.subscriptionConfig.products[productCode] = {
        id: product.productId,
        quantity: 0,
        words: product.words,
        addOns: {},
      }
    })
    this.addOnCodes.forEach((addOnCode) => {
      this.subscriptionConfig.addOns[addOnCode] = {
        checked: false,
        disabled: false,
      }
    })
    this.prevPlusPhotoChecked = false
    this.plusValetChecked = false
    this.showUpTo = 1

    if (subscriptionToLoad) {
      this.showUpTo = 99
      this.subscriptionConfig.durationInMonths = subscriptionToLoad.durationInMonths
      this.prevCurrentPeriodEndsAt = moment(subscriptionToLoad.currentPeriodEndsAt)

      if (!subscriptionToLoad.selectedProducts.length) return

      let customProducts: ISelectedContent[] = subscriptionToLoad.selectedProducts
        .filter((product) => !this.productCodesToShow.includes(product.code))
        .sort(
          (a: ISelectedProduct, b: ISelectedProduct) =>
            Number(a.code.split('content')[1]) - Number(b.code.split('content')[1])
        )

      customProducts.forEach((product: ISelectedContent) => {
        const productAlreadyPushed = this.customWordCounts.length === customProducts.length

        if (productAlreadyPushed) return
        this.loadCustomWordCount(product)
      })

      subscriptionToLoad.selectedProducts
        .filter((product: ISelectedContent) => this.productCodesToShow.includes(product.code))
        .forEach((product: ISelectedContent) => {
          if (product.code.startsWith('content')) {
            this.subscriptionConfig.products[product.code] = {
              id: product.id,
              quantity: product.quantity,
            }
            product.selectedAddOns?.forEach((addOn) => {
              // this validation is because prevPlusPhotoChecked change the value of the checkbox
              if (addOn.code === 'photo') this.handleAddOnChange('photo')
              if (this.subscriptionConfig.addOns[addOn.code])
                this.subscriptionConfig.addOns[addOn.code].checked = true
            })
          } else if (product.code == 'valet') {
            this.plusValetChecked = true
          }
        })
    }
  }

  private updateSummaryData() {
    this.summaryData = {
      priceListLoaded: this.priceListLoaded,
      summaryModifier: this.summaryModifier,
      totalCredits: this.totalCredits,
      subTotalFormattedValue: this.subTotalFormattedValue,
      valetFormattedValue: this.valetFormattedValue,
      discountsFormattedValue: this.discountsFormattedValue,
      totalFormattedValue: this.totalFormattedValue,
      plusValetChecked: this.plusValetChecked,
      durationInMonths: this.subscriptionConfig.durationInMonths,
      discountApplied: this.discountApplied,
      hasCoupons: this.hasCoupons,
      usableCoupons: this.usableCoupons,
      subTotalWithoutCouponsOrValet: this.subTotalWithoutCouponsOrValet,
      confirmationMessage1: this.confirmationMessage1,
      confirmationMessage2: this.confirmationMessage2,
      customerId: this.customerId,
      actionButton: this.actionButton,
    }
  }

  private currencyFormat(valueInCents: number): string {
    const cents = prettyCents(valueInCents)
    return '$' + prettyDollars(valueInCents) + (cents ? '.' + cents : '')
  }

  get isMobile(): boolean {
    return this.mediaQuery.isUnder('lg')
  }

  setPastDue(): boolean {
    if (this.customer && this.customer.subscription) {
      return this.customer.state !== 'expired' && !!this.customer.subscription.pastDueAt
    } else {
      return false
    }
  }

  changesScheduled(): boolean {
    return (
      !!this.customer?.scheduledSubscription || !!this.customer?.subscription?.scheduledToCancelAt
    )
  }

  dashboardAccessUntil(): Date {
    return moment(this.customer?.expiresAt).toDate()
  }

  private setUsableCoupons(): void {
    if (this.customer && this.account) {
      // This should apply to all types in the future, not only percent
      this.usableCoupons = availableCouponApplications(this.customer, this.account)
        .filter((ca) => ca.usable && ca.coupon.discountType === 'percent')
        .map((ca) => ca.coupon)
    } else {
      this.usableCoupons = []
    }
    this.hasCoupons = this.usableCoupons.length > 0
  }

  private percentCouponDiscount(coupon: Coupon): number {
    let discountInCents = (this.subTotalWithoutCouponsOrValet * coupon.discountPercent) / 100

    if (coupon.dollarLimit) {
      let discountLimitInCents = coupon.dollarLimit * 100
      discountInCents =
        discountInCents > discountLimitInCents ? discountLimitInCents : discountInCents
    }

    return discountInCents
  }
}
